import React, { useState } from 'react';
import FeedbackForm from './FeedbackForm';
import FeedbackReport from './FeedbackReport';
import PerformanceAnalyzer from './PerformanceAnalyzer';
import SurveyForm from './SurveyForm';
import SurveyReport from './SurveyReport';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonMenuButton
} from '@ionic/react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  Typography,
  Container,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Feedback,
  Assessment,
  Analytics,
  Poll,
  TrendingUp
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`performance-tabpanel-${index}`}
      aria-labelledby={`performance-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const MarkPerformance = () => {
  const [currentView, setCurrentView] = useState('feedback');
  const [token] = useState(localStorage.getItem('authToken') || '');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentView(newValue);
  };

  const tabItems = [
    {
      value: 'feedback',
      label: 'Submit Feedback',
      icon: <Feedback />,
      component: <FeedbackForm token={token} />
    },
    {
      value: 'feedback-report',
      label: 'Feedback Report',
      icon: <Assessment />,
      component: <FeedbackReport token={token} />
    },
    {
      value: 'survey',
      label: 'Submit Survey',
      icon: <Poll />,
      component: <SurveyForm token={token} />
    },
    {
      value: 'survey-report',
      label: 'Survey Report',
      icon: <Analytics />,
      component: <SurveyReport token={token} />
    },
    {
      value: 'analyzer',
      label: 'Analyze Scores',
      icon: <TrendingUp />,
      component: <PerformanceAnalyzer token={token} />
    }
  ];

  return (
    <IonPage className='pushDown'>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Performance Management</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
          <AppBar 
            position="static" 
            elevation={0}
            sx={{ 
              bgcolor: 'primary.main',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
            }}
          >
            <Toolbar>
              <Typography variant="h4" component="h1" sx={{ 
                fontWeight: 700,
                color: 'white',
                flexGrow: 1
              }}>
                Performance Center
              </Typography>
            </Toolbar>
          </AppBar>

          <Container maxWidth="xl" sx={{ py: 3 }}>
            <Paper 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                overflow: 'hidden',
                mb: 3
              }}
            >
              <Tabs
                value={currentView}
                onChange={handleTabChange}
                variant={isMobile ? "scrollable" : "fullWidth"}
                scrollButtons="auto"
                aria-label="performance tabs"
                sx={{
                  bgcolor: 'grey.50',
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    minHeight: 64,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: 'primary.main',
                    }
                  }
                }}
              >
                {tabItems.map((tab) => (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    icon={tab.icon}
                    label={isMobile ? null : tab.label}
                    aria-label={tab.label}
                    sx={{
                      minWidth: isMobile ? 80 : undefined,
                      ...(isMobile && {
                        '& .MuiTab-iconWrapper': {
                          mb: 0.5
                        }
                      })
                    }}
                  />
                ))}
              </Tabs>

              <Box sx={{ p: 3 }}>
                {tabItems.map((tab) => (
                  <TabPanel key={tab.value} value={currentView} index={tab.value}>
                    {tab.component}
                  </TabPanel>
                ))}
              </Box>
            </Paper>

            {/* Quick Stats Overview */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Feedback color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" color="primary">
                  Feedback
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Share constructive feedback
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Poll color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" color="secondary">
                  Surveys
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete performance surveys
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Analytics color="success" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" color="success.main">
                  Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Analyze performance data
                </Typography>
              </Paper>
            </Box>
          </Container>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default MarkPerformance;